import { TransitionManager } from '../utils/TransitionManager';
import { UserStore } from '../../../auth/model/UserStore';
import { AuthState } from '../../../auth/AuthState';
import { AppEvents } from '@Engage.Core';
import { VariableReplacer } from '../VariableReplacer';
import { ActivationEngine } from '../activation/ActivationEngine';
export class BaseTemplate {
    constructor(action) {
        var _a;
        this.action = action;
        this.container = document.createElement('div');
        // Mg2-connext + data-display-type are required for ConnextV2 CSS scoping
        // (closebtn, closebtn-pos-*, modal styles etc. all depend on this)
        this.container.className = 'engage-template Mg2-connext';
        const displayType = ((_a = action === null || action === void 0 ? void 0 : action.showWhat) === null || _a === void 0 ? void 0 : _a.TemplateDeviceSettings) || 'Default';
        this.container.setAttribute('data-display-type', displayType.toLowerCase());
    }
    /**
     * Returns the action HTML with all {{variable}} placeholders replaced.
     * Mirrors Connext v2: dom.parseVariables(action.What.Html, ActionModule.getReplaceVariables())
     */
    getProcessedHtml() {
        var _a, _b;
        return VariableReplacer.process((_b = (_a = this.action) === null || _a === void 0 ? void 0 : _a.html) !== null && _b !== void 0 ? _b : '');
    }
    mountToBody() {
        document.body.appendChild(this.container);
        this.setupButtonClickTracking();
        // Dispatch ActionShow — mirrors Connext v2 Action.show()
        document.dispatchEvent(new CustomEvent(AppEvents.ActionShow, {
            detail: this.action,
            bubbles: true
        }));
    }
    /**
     * Show/hide login, register, logout elements based on auth state.
     * Mirrors ConnextV2 — applies both to the template container and the full page.
     */
    applyUserState() {
        const loggedIn = this.isUserLoggedIn();
        // apply to the rendered template container
        this.container.querySelectorAll('[data-mg2-action="login"]')
            .forEach(el => { el.style.display = loggedIn ? 'none' : ''; });
        this.container.querySelectorAll('[data-mg2-action="register"]')
            .forEach(el => { el.style.display = loggedIn ? 'none' : ''; });
        this.container.querySelectorAll('[data-mg2-action="logout"]')
            .forEach(el => { el.style.display = loggedIn ? '' : 'none'; });
        // also sync the full page (elements outside the template)
        document.querySelectorAll('[data-mg2-action="login"]')
            .forEach(el => { el.style.display = loggedIn ? 'none' : ''; });
        document.querySelectorAll('[data-mg2-action="register"]')
            .forEach(el => { el.style.display = loggedIn ? 'none' : ''; });
        document.querySelectorAll('[data-mg2-action="logout"]')
            .forEach(el => { el.style.display = loggedIn ? '' : 'none'; });
    }
    /**
     * Track clicks on [data-mg2-action="click"] buttons and bind activation.
     * Dispatches onButtonClick — mirrors Connext v2 eventsService.dispatch(AppEvents.ButtonClick).
     */
    setupButtonClickTracking() {
        this.container.querySelectorAll('[data-mg2-action="click"]')
            .forEach(btn => {
            btn.addEventListener('click', () => {
                document.dispatchEvent(new CustomEvent(AppEvents.ButtonClick, {
                    detail: this.action,
                    bubbles: true
                }));
            });
        });
        // Bind activation click — mirrors Connext v2 ActivationMg2Action
        this.container.querySelectorAll('[data-mg2-action="activation"]')
            .forEach(btn => {
            btn.setAttribute('data-engage-activation-bound', 'true');
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                ActivationEngine.process();
            });
        });
    }
    /**
     * Bind all close buttons — matches ConneXt v2 Template.addCloseBtnListener().
     * Handles [data-dismiss] and [_data-dismiss], href="#" or no href → hide,
     * href with value → navigate or open in new tab.
     */
    setupCloseHandler(onClose) {
        // match [data-dismiss], [_data-dismiss], and .closebtn (ConnextV2 template pattern)
        const closeButtons = this.container.querySelectorAll('[data-dismiss], [_data-dismiss], .closebtn, .fa-times');
        closeButtons.forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                const href = btn.getAttribute('href');
                // not-closable paywall guard
                const dismissVal = btn.getAttribute('data-dismiss') || btn.getAttribute('_data-dismiss');
                if (dismissVal === 'not-closable-paywall') {
                    if (!href || href === '#')
                        return;
                }
                if (!href || href === '#') {
                    this.applyTransitionOut(() => {
                        this.container.remove();
                        document.dispatchEvent(new CustomEvent(AppEvents.ActionClosed, {
                            detail: this.action,
                            bubbles: true
                        }));
                        onClose === null || onClose === void 0 ? void 0 : onClose();
                    });
                }
                else if (btn.hasAttribute('target')) {
                    window.open(href, '_blank');
                }
                else {
                    window.location.href = href;
                }
            });
        });
    }
    applyTransitionIn() {
        var _a;
        const showWhat = (_a = this.action) === null || _a === void 0 ? void 0 : _a.showWhat;
        if (showWhat === null || showWhat === void 0 ? void 0 : showWhat.TransitionButton) {
            TransitionManager.enter(this.container, showWhat.InputTransition, showWhat.InputSpeed);
        }
    }
    applyTransitionOut(callback) {
        var _a;
        const showWhat = (_a = this.action) === null || _a === void 0 ? void 0 : _a.showWhat;
        if (showWhat === null || showWhat === void 0 ? void 0 : showWhat.TransitionButton) {
            TransitionManager.exit(this.container, showWhat.OutputTransition, showWhat.OutputSpeed, callback);
        }
        else {
            callback === null || callback === void 0 ? void 0 : callback();
        }
    }
    // ─── User state helper ────────────────────────────────────────────────────
    isUserLoggedIn() {
        // AuthState is set synchronously during Auth0Plugin.init() (restoreSession/seamlessLogin)
        // so it's reliable even before UserStore is fully populated.
        // Fall back to UserStore for cases where Auth0 is not used.
        return AuthState.isAuthenticated || UserStore.isLoggedIn();
    }
    /**
     * Re-creates <script> tags so the browser executes them.
     * innerHTML does not run scripts — this mirrors Connext v2 behaviour where
     * Bootstrap/jQuery evaluates inline scripts after DOM insertion.
     *
     * Scripts with data-remove="true" are removed from the DOM after execution
     * (matches the Connext v2 template pattern).
     */
    executeScripts(container) {
        const scripts = container.querySelectorAll('script');
        scripts.forEach(oldScript => {
            var _a;
            const newScript = document.createElement('script');
            // Copy attributes
            Array.from(oldScript.attributes).forEach(attr => {
                newScript.setAttribute(attr.name, attr.value);
            });
            // Copy inline content
            newScript.textContent = oldScript.textContent;
            // Replace old (inert) script with new (executable) one
            (_a = oldScript.parentNode) === null || _a === void 0 ? void 0 : _a.replaceChild(newScript, oldScript);
            // Remove from DOM after execution if data-remove="true"
            if (newScript.getAttribute('data-remove') === 'true') {
                newScript.remove();
            }
        });
    }
}
